import { useEffect } from "react";
import CustomInput from "../../components/CustomInput";
import ToggleSwitch from "../../components/ToggleSwitch";
import {
  SchoolBranch,
  UpdateSchoolBranchPayload,
} from "../../types/schoolBranchType";
import { useFormEdit } from "../../util/hooks/useFormEdit";
import { MONTHS } from "../../util/Constants";
import CustomSelect from "../../components/CustomSelect";

interface TimingProps {
  branch: SchoolBranch | undefined;
  isLoading: boolean;
  onDataChange: (data: Partial<UpdateSchoolBranchPayload>) => void;
}

export default function SchoolTiming({
  branch,
  isLoading,
  onDataChange,
}: TimingProps) {
  const { formData, setFormData, isEditing, hasUnsavedChanges } =
    useFormEdit<UpdateSchoolBranchPayload>({} as UpdateSchoolBranchPayload);

  useEffect(() => {
    if (branch) {
      setFormData({
        startTime: branch.startTime || "",
        endTime: branch.endTime || "",
        academicYearStart: branch.academicYearStart || "",
        academicYearEnd: branch.academicYearEnd || "",
      });
    }
  }, [branch]);

  const dayKeyMap: Record<string, keyof SchoolBranch> = {
    Sunday: "isSun",
    Monday: "isMon",
    Tuesday: "isTue",
    Wednesday: "isWed",
    Thursday: "isThu",
    Friday: "isFri",
    Saturday: "isSat",
  };

  const handleInputChange = (event: React.ChangeEvent<any>) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value }; // still string in form
      onDataChange({ [name]: value }); // propagate string
      return updated;
    });
  };

  return (
    <div className="grid gap-14 px-4 md:px-9 py-7">
      <form className="grid md:grid-cols-2 gap-7">
        <CustomInput
          name="startTime"
          label="School start timing"
          type="time"
          readOnly={!isEditing}
          value={
            typeof formData.startTime === "string"
              ? formData.startTime
              : formData.startTime
                ? `${String(formData.startTime.hour).padStart(2, "0")}:${String(
                    formData.startTime.minute,
                  ).padStart(2, "0")}`
                : ""
          }
          onChange={handleInputChange}
          inputClassName="w-full"
        />
        <CustomInput
          name="endTime"
          label="School End timing"
          type="time"
          readOnly={!isEditing}
          value={
            typeof formData.endTime === "string"
              ? formData.endTime
              : formData.endTime
                ? `${String(formData.endTime.hour).padStart(2, "0")}:${String(
                    formData.endTime.minute,
                  ).padStart(2, "0")}`
                : ""
          }
          onChange={handleInputChange}
          inputClassName="w-full"
        />

        <CustomSelect
          name="academicYearStart"
          label="Academic Year Start"
          value={formData.academicYearStart || ""}
          onChange={handleInputChange}
          readOnly={!isEditing}
          options={MONTHS}
          placeholder="Select Start Month"
        />

        <CustomSelect
          name="academicYearEnd"
          label="Academic Year End"
          value={formData.academicYearEnd || ""}
          onChange={handleInputChange}
          readOnly={!isEditing}
          options={MONTHS}
          placeholder="Select End Month"
        />
      </form>
      <div className="flex flex-col gap-8 overflow-x-auto">
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-medium text-neutral-400">
            Days of Week
          </h3>
          <span className="text-xs font-medium text-error-500">
            *Please enable the days on which the school runs
          </span>
        </div>
        <div className="columns-1 md:columns-2 gap-x-24 max-w-4xl">
          {Object.keys(dayKeyMap).map((day) => (
            <div
              key={day}
              className="flex items-center justify-between text-center space-y-6 break-inside-avoid"
            >
              <span className="font-semibold text-neutral-400">{day}</span>
              <ToggleSwitch
                defaultChecked={Boolean(branch?.[dayKeyMap[day]])}
                disabled={!isEditing}
                onToggle={(checked) =>
                  onDataChange({ [dayKeyMap[day]]: checked })
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
